// apps/web/src/pages/BankDetailPage.tsx
// 题库详情页 — 参考微信小程序风格重构

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import toast from "react-hot-toast";
import { getBank, getBankStatsApi, deleteBankApi, updateBankApi } from "@/api/banks";
import type { BankDTO } from "@examfast/shared-types";

export default function BankDetailPage() {
  const { bankId } = useParams<{ bankId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const menuRef = useRef<HTMLDivElement>(null);

  const { data: bank, isLoading, isError } = useQuery({
    queryKey: ["bank", bankId],
    queryFn: () => getBank(bankId!),
    enabled: !!bankId,
  });

  const { data: stats } = useQuery({
    queryKey: ["bank", bankId, "stats"],
    queryFn: () => getBankStatsApi(bankId!),
    enabled: !!bankId,
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteBankApi(bankId!),
    onSuccess: () => {
      toast.success("题库已删除");
      queryClient.invalidateQueries({ queryKey: ["banks"] });
      navigate("/banks", { replace: true });
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "删除失败");
    },
  });

  const renameMutation = useMutation({
    mutationFn: (name: string) => updateBankApi(bankId!, { name }),
    onSuccess: (updatedBank) => {
      toast.success("题库名称已更新");
      queryClient.setQueryData(["bank", bankId], updatedBank);
      queryClient.invalidateQueries({ queryKey: ["banks"] });
      setShowRenameDialog(false);
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "修改失败");
    },
  });

  // 点击外部关闭菜单
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    }
    if (showMenu) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showMenu]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isError || !bank) {
    return (
      <div className="p-4 text-center text-danger py-10">
        加载题库失败
      </div>
    );
  }

  const menuItems = [
    {
      label: "修改名称",
      icon: <EditIcon />,
      onClick: () => {
        setRenameValue(bank.name);
        setShowRenameDialog(true);
        setShowMenu(false);
      },
    },
    { label: "AI 导题", icon: <SparklesIcon />, onClick: () => { navigate(`/import?bankId=${bankId}`); setShowMenu(false); } },
    { label: "删除题库", icon: <TrashIcon />, danger: true, onClick: () => { setShowDeleteConfirm(true); setShowMenu(false); } },
  ];

  return (
    <div className="min-h-screen bg-surface pb-24">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="p-1 -ml-1 text-gray-700 hover:text-gray-900 btn-tap"
          aria-label="返回"
        >
          <ChevronLeftIcon />
        </button>
        <h1 className="text-base font-semibold text-gray-900">题库详情</h1>
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowMenu((s) => !s)}
            className="p-1 -mr-1 text-gray-700 hover:text-gray-900 btn-tap"
            aria-label="更多"
          >
            <MoreIcon />
          </button>
          {showMenu && (
            <div className="absolute right-0 top-full mt-2 w-36 bg-white rounded-xl shadow-card-hover border border-gray-100 py-1 overflow-hidden">
              {menuItems.map((item) => (
                <button
                  key={item.label}
                  onClick={item.onClick}
                  className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${
                    item.danger ? "text-danger hover:bg-danger/5" : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <span className="w-4 h-4">{item.icon}</span>
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        {/* 题库信息卡 */}
        <div className="bg-white rounded-2xl shadow-card px-4 py-4">
          <h2 className="text-[17px] font-bold text-gray-900 leading-tight">{bank.name}</h2>
          {bank.tag && (
            <span className="mt-1.5 inline-block text-[11px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
              {bank.tag}
            </span>
          )}
          <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-500">
            <div className="flex items-center gap-1">
              <DocIcon />
              <span>{bank.question_count} 道</span>
            </div>
            <div className="flex items-center gap-1">
              <ClockMiniIcon />
              <span>更新 {formatDate(bank.updated_at)}</span>
            </div>
          </div>
          {bank.description && (
            <p className="mt-2.5 text-sm text-gray-500 leading-6 line-clamp-2">
              {bank.description}
            </p>
          )}
        </div>

        {/* 功能入口：错题 / 收藏 / 笔记 */}
        <div className="grid grid-cols-3 gap-2.5">
          <FeatureCard
            icon={<XCircleIcon className="text-red-500" />}
            label="我的错题"
            count={stats?.wrong_count ?? 0}
            to={`/wrong?bankId=${bankId}`}
          />
          <FeatureCard
            icon={<StarIcon className="text-yellow-500" />}
            label="我的收藏"
            count={stats?.favorite_count ?? 0}
            to={`/favorites?bankId=${bankId}`}
          />
          <FeatureCard
            icon={<NoteIcon className="text-blue-500" />}
            label="我的笔记"
            count={stats?.note_count ?? 0}
            to={`/notes?bankId=${bankId}`}
          />
        </div>

        {/* 练习统计 */}
        <div className="bg-white rounded-2xl shadow-card px-4 py-4">
          <div className="grid grid-cols-4 gap-1.5 text-center">
            <StatItem
              label="完成率"
              value={`${stats?.completion_rate ?? 0}%`}
              color="text-emerald-500"
            />
            <StatItem
              label="正确率"
              value={`${stats?.accuracy_rate ?? 0}%`}
              color="text-emerald-500"
            />
            <StatItem
              label="答对"
              value={`${stats?.correct_count ?? 0}题`}
              color="text-emerald-500"
            />
            <StatItem
              label="答错"
              value={`${stats?.wrong_answer_count ?? 0}题`}
              color="text-red-500"
            />
          </div>
        </div>

        {/* 题库练习 */}
        <div>
          <h3 className="mb-2.5 text-sm font-semibold text-gray-900">题库练习</h3>
          <div className="grid grid-cols-2 gap-2.5">
            <PracticeCard
              icon={<ListIcon />}
              title="顺序练习"
              desc={`已练 ${Math.min(stats?.answered_count ?? 0, bank.question_count)}/${bank.question_count}`}
              to={`/banks/${bankId}/practice/sequential`}
              color="bg-orange-50 text-orange-500"
            />
            <PracticeCard
              icon={<ShuffleIcon />}
              title="随机练习"
              desc="自定义设置练习量"
              to={`/banks/${bankId}/practice/random`}
              color="bg-indigo-50 text-indigo-500"
            />
            <PracticeCard
              icon={<ClockIcon />}
              title="模拟考试"
              desc="仿真模拟"
              to={`/banks/${bankId}/practice/mock`}
              color="bg-cyan-50 text-cyan-500"
            />
            <PracticeCard
              icon={<FilterIcon />}
              title="题型练习"
              desc="按题型分类练习"
              to={`/banks/${bankId}/practice/type-select`}
              color="bg-blue-50 text-blue-500"
            />
            <PracticeCard
              icon={<BookIcon />}
              title="章节练习"
              desc="按章节专项练习"
              onClick={() => toast("章节练习即将上线", { icon: "🚧" })}
              color="bg-purple-50 text-purple-500"
            />
            <PracticeCard
              icon={<SearchIcon />}
              title="题库搜索"
              desc="精准搜索题库试题"
              to={`/search?bankId=${bankId}`}
              color="bg-green-50 text-green-500"
            />
          </div>
        </div>

        {/* 题目管理 */}
        <div className="bg-white rounded-2xl shadow-card px-4 py-3.5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">题目管理</h3>
              <p className="text-xs text-gray-400 mt-0.5">共 {bank.question_count} 题</p>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to={`/banks/${bankId}/questions/new`}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-primary text-white hover:bg-primary-dark btn-tap"
              >
                + 添加题目
              </Link>
              <Link
                to={`/import?bankId=${bankId}`}
                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-primary text-primary hover:bg-primary/5 btn-tap"
              >
                AI 导入
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirm dialog */}
      {showRenameDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-6">
          <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-card-hover">
            <h3 className="mb-2 text-base font-bold text-gray-900">修改题库名称</h3>
            <p className="mb-4 text-sm text-gray-500">更新后的名称会同步显示在题库列表和详情页。</p>
            <input
              type="text"
              value={renameValue}
              onChange={(event) => setRenameValue(event.target.value)}
              maxLength={100}
              placeholder="请输入题库名称"
              className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
            />
            <div className="mt-4 flex gap-3">
              <button
                onClick={() => {
                  setShowRenameDialog(false);
                  setRenameValue(bank.name);
                }}
                className="flex-1 rounded-xl bg-gray-100 py-2.5 text-sm font-medium text-gray-700 btn-tap"
              >
                取消
              </button>
              <button
                onClick={() => {
                  const nextName = renameValue.trim();
                  if (!nextName) {
                    toast.error("题库名称不能为空");
                    return;
                  }
                  if (nextName === bank.name) {
                    setShowRenameDialog(false);
                    return;
                  }
                  renameMutation.mutate(nextName);
                }}
                disabled={renameMutation.isPending}
                className="flex-1 rounded-xl bg-primary py-2.5 text-sm font-medium text-white disabled:opacity-50 btn-tap"
              >
                {renameMutation.isPending ? "保存中..." : "保存"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-6">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-card-hover">
            <h3 className="text-base font-bold text-gray-900 mb-2">确认删除</h3>
            <p className="text-sm text-gray-500 mb-4">
              删除「{bank.name}」及其所有题目，此操作不可恢复。
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium btn-tap"
              >
                取消
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="flex-1 py-2.5 bg-danger text-white rounded-xl text-sm font-medium
                           disabled:opacity-50 btn-tap"
              >
                {deleteMutation.isPending ? "删除中..." : "删除"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ========== 子组件 ==========

function FeatureCard({
  icon,
  label,
  count,
  to,
}: {
  icon: React.ReactNode;
  label: string;
  count: number;
  to: string;
}) {
  return (
    <Link
      to={to}
      className="flex flex-col items-center gap-1.5 bg-white rounded-2xl px-3 py-3.5 shadow-card btn-tap"
    >
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-50">
        {icon}
      </div>
      <span className="text-[12px] text-gray-600">{label}</span>
      <span className="text-[15px] font-semibold text-gray-900">{count}</span>
    </Link>
  );
}

function StatItem({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div className="flex flex-col items-center">
      <span className={`text-[16px] font-bold ${color}`}>{value}</span>
      <span className="mt-1 text-[12px] text-gray-400">{label}</span>
    </div>
  );
}

function PracticeCard({
  icon,
  title,
  desc,
  to,
  onClick,
  color,
}: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  to?: string;
  onClick?: () => void;
  color: string;
}) {
  const base =
    "flex items-center gap-3 rounded-2xl bg-white px-3.5 py-3.5 shadow-card transition-all btn-tap";
  const body = (
    <>
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${color}`}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        <p className="mt-0.5 text-[12px] text-gray-400 truncate">{desc}</p>
      </div>
    </>
  );

  if (to) {
    return (
      <Link to={to} className={base}>
        {body}
      </Link>
    );
  }
  return (
    <button onClick={onClick} className={`${base} w-full text-left`}>
      {body}
    </button>
  );
}

// ========== Icons ==========

function ChevronLeftIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="5" r="1.5" fill="currentColor" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      <circle cx="12" cy="19" r="1.5" fill="currentColor" />
    </svg>
  );
}

function SparklesIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m12 3-1.5 4.5L6 9l4.5 1.5L12 15l1.5-4.5L18 9l-4.5-1.5L12 3Z" />
      <path d="M5 16l-1 3-1-3-3-1 3-1 1-3 1 3 3 1-3 1Z" />
      <path d="M19 16l-1 3-1-3-3-1 3-1 1-3 1 3 3 1-3 1Z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function DocIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  );
}

function ClockMiniIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function XCircleIcon({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={`w-5 h-5 ${className}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  );
}

function StarIcon({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={`w-5 h-5 ${className}`} fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function NoteIcon({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={`w-5 h-5 ${className}`} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  );
}

function ShuffleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 3 21 3 21 8" />
      <line x1="4" y1="20" x2="21" y2="3" />
      <polyline points="21 16 21 21 16 21" />
      <line x1="15" y1="15" x2="21" y2="21" />
      <line x1="4" y1="4" x2="9" y2="9" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

function BookIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />
    </svg>
  );
}

function formatDate(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
